// src/components/Settings/SignatureUploader.tsx
//
// Upload, preview, and save a signature image to Supabase Storage. The saved
// signature prints on every contract: the Authorized Signature line and the
// ST-124 (ContractPage).
// Includes automatic background cleanup (threshold + trim) for signatures
// captured against dark backgrounds or photos of paper.
//
// Wire-up:
//   <SignatureUploader
//     currentUrl={settings.signatureUrl}
//     onSaved={(url) => updateSettings({ signatureUrl: url })}
//   />

import { useEffect, useRef, useState } from "react";
import { supabase } from "../../supabaseClient";

interface Props {
  currentUrl?: string | null;
  /** Called once the cleaned PNG has been uploaded to Supabase Storage */
  onSaved: (publicUrl: string) => void;
  /** Supabase Storage bucket name. Must exist and allow uploads. */
  bucket?: string;
}

/** Where a user's signature lives: a folder named after their auth user id.
 *  Storage policies only let a signed-in user write inside their OWN folder,
 *  so one person can never replace another's signature. (Until 2026-09-24
 *  every user wrote the single shared contractor-signature.png — whoever
 *  uploaded last signed everyone's contracts. That file is left in place so
 *  links saved before this keep loading; nobody can write to it any more.) */
export function signatureObjectPath(userId: string): string {
  return `${userId}/signature.png`;
}

export function SignatureUploader({
  currentUrl,
  onSaved,
  bucket = "signatures",
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentUrl ?? null);
  const [hasNewClean, setHasNewClean] = useState(false);
  const cleanedBlobRef = useRef<Blob | null>(null);

  // Show the saved signature on first load
  useEffect(() => {
    if (currentUrl && previewRef.current) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = previewRef.current!;
        const maxW = 400;
        const scale = Math.min(1, maxW / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext("2d")!;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.src = currentUrl;
    }
  }, [currentUrl]);

  async function handleFile(file: File) {
    setBusy(true);
    setStatus("Cleaning up signature…");
    try {
      const cleaned = await cleanSignature(file);
      cleanedBlobRef.current = cleaned;
      setPreviewUrl(URL.createObjectURL(cleaned));
      setHasNewClean(true);
      setStatus("Looks good? Click Save to upload.");
    } catch (err: any) {
      setStatus(`Error processing image: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleSave() {
    if (!cleanedBlobRef.current) return;
    setBusy(true);
    setStatus("Uploading…");
    try {
      // The folder must be the id in the token the upload is sent with — that
      // is exactly what the storage policy compares against.
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error("sign in to save a signature");
      const storagePath = signatureObjectPath(userId);
      const { error: upErr } = await supabase.storage
        .from(bucket)
        .upload(storagePath, cleanedBlobRef.current, {
          contentType: "image/png",
          upsert: true,
          cacheControl: "0", // force re-fetch after re-upload
        });
      if (upErr) throw upErr;

      const { data } = supabase.storage.from(bucket).getPublicUrl(storagePath);
      // Append a cache-busting query so newly-uploaded sigs show immediately
      const url = `${data.publicUrl}?t=${Date.now()}`;
      onSaved(url);
      setHasNewClean(false);
      setStatus("Saved.");
    } catch (err: any) {
      setStatus(`Upload failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  function handleClear() {
    cleanedBlobRef.current = null;
    setPreviewUrl(null);
    setHasNewClean(false);
    setStatus("");
    if (fileRef.current) fileRef.current.value = "";
    const canvas = previewRef.current;
    if (canvas) canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 480 }}>
      <label style={{ fontWeight: 600 }}>Signature</label>
      <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
        Upload a PNG or JPG of your signature. The background will be cleaned automatically.
        Black or dark ink on any background works best.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg"
        disabled={busy}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />

      <div style={{
        border: "1px dashed #ccc",
        borderRadius: 6,
        padding: 8,
        minHeight: 80,
        background: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        {previewUrl ? (
          // After upload: show the URL-based preview (uses canvas for currentUrl, img for new clean)
          hasNewClean ? (
            <img src={previewUrl} alt="signature preview" style={{ maxWidth: "100%", maxHeight: 160 }} />
          ) : (
            <canvas ref={previewRef} style={{ maxWidth: "100%", maxHeight: 160 }} />
          )
        ) : (
          <span style={{ color: "#999", fontSize: 13 }}>No signature uploaded</span>
        )}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={handleSave} disabled={busy || !hasNewClean}>
          {busy ? "Working…" : "Save"}
        </button>
        <button onClick={handleClear} disabled={busy}>Clear</button>
      </div>

      {status && <div style={{ fontSize: 13, color: "#444" }}>{status}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Signature cleanup — runs in-browser via canvas
//
// Strategy:
//   1. Decide whether image is "dark ink on light bg" (most photos/scans)
//      or "light ink on dark bg" (the remove.bg case).
//   2. Convert to grayscale, threshold to a binary mask.
//   3. Set ink pixels to opaque black; everything else to transparent.
//   4. Trim transparent borders.
// ---------------------------------------------------------------------------
async function cleanSignature(file: File): Promise<Blob> {
  const img = await loadImageFromFile(file);

  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // Average luminance to decide ink-on-light vs light-on-dark
  let total = 0;
  for (let i = 0; i < data.length; i += 4) {
    total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }
  const avgL = total / (data.length / 4);
  const darkBg = avgL < 90;

  // Threshold to a binary mask
  for (let i = 0; i < data.length; i += 4) {
    const l = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    const isInk = darkBg ? l > 30 : l < 180;
    if (isInk) {
      data[i] = 0; data[i + 1] = 0; data[i + 2] = 0; data[i + 3] = 255;
    } else {
      data[i + 3] = 0; // transparent
    }
  }
  ctx.putImageData(imageData, 0, 0);

  // Trim transparent border, then trim excessive descenders
  const trimmed = trimTransparent(canvas);
  const compact = capAspectRatio(trimmed, 2.2); // body + modest descender
  return await new Promise<Blob>((resolve, reject) => {
    compact.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png");
  });
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = URL.createObjectURL(file);
  });
}

function trimTransparent(src: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = src.getContext("2d", { willReadFrequently: true })!;
  const { width: w, height: h } = src;
  const data = ctx.getImageData(0, 0, w, h).data;

  let top = h, bottom = -1, left = w, right = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 0) {
        if (y < top) top = y;
        if (y > bottom) bottom = y;
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }
  if (right < 0) return src; // empty
  const pad = 10;
  const x = Math.max(0, left - pad);
  const y = Math.max(0, top - pad);
  const tw = Math.min(w, right + pad) - x;
  const th = Math.min(h, bottom + pad) - y;

  const out = document.createElement("canvas");
  out.width = tw;
  out.height = th;
  out.getContext("2d")!.drawImage(src, x, y, tw, th, 0, 0, tw, th);
  return out;
}

/**
 * Ensure the image is at most `maxRatio` times wider than tall.
 * If too tall (i.e. long descenders), crop from the bottom while keeping a
 * short descender. This prevents the signature from rendering too tall on the
 * ST-124 / contract page.
 */
function capAspectRatio(src: HTMLCanvasElement, maxRatio: number): HTMLCanvasElement {
  const aspect = src.width / src.height;
  if (aspect >= maxRatio) return src;

  // We need to keep `keepHeight = width / maxRatio` rows.
  // Bias toward the top of the image (which has the main body of the signature).
  const ctx = src.getContext("2d", { willReadFrequently: true })!;
  const { width: w, height: h } = src;
  const data = ctx.getImageData(0, 0, w, h).data;

  // Find ink density per row
  const density = new Array<number>(h).fill(0);
  for (let y = 0; y < h; y++) {
    let count = 0;
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 0) count++;
    }
    density[y] = count;
  }

  // Peak density and threshold (~25% of peak = "body" region)
  const peak = Math.max(...density);
  const bodyThreshold = peak * 0.25;
  let bodyEnd = 0;
  for (let y = h - 1; y >= 0; y--) {
    if (density[y] >= bodyThreshold) {
      bodyEnd = y;
      break;
    }
  }

  // Keep body + 20% more rows for a short descender
  const descenderRows = Math.round((bodyEnd + 1) * 0.2);
  let keepHeight = Math.min(h, bodyEnd + 1 + descenderRows);

  // Also enforce the max aspect ratio constraint
  const minHeight = Math.ceil(w / maxRatio);
  keepHeight = Math.max(minHeight, keepHeight);
  keepHeight = Math.min(h, keepHeight);

  if (keepHeight >= h) return src;

  const out = document.createElement("canvas");
  out.width = w;
  out.height = keepHeight;
  out.getContext("2d")!.drawImage(src, 0, 0, w, keepHeight, 0, 0, w, keepHeight);
  return out;
}
